/**
 * @typedef {Object} ServerJsonResponse
 * @property {"success"|"error"|"warning"} status
 * @property {string} message
 * @property {any} data
 */

/**
 * Tipos de eventos disparados relacionados ao servidor
 */
const ServerEvents = {
    ADD_PALESTRANTE_ORCAMENTO: 'add-palestrante-orcamento',
    REMOVE_PALESTRANTE_ORCAMENTO: 'remove-palestrante-orcamento'
};

/**
 * Classe responsavel por realizar as requisições que executam ações no servidor
 */
const ServerActions = {
    /**
     * Executa uma requisicao GET no servidor e retorna o HTML da resposta
     * @param {string} action   Nome da ação registrada no servidor a ser executada
     * @param {*} data          Dados a serem enviados para o servidor
     * @returns {Promise<string>}
     */
    get: async (action, data) => {
        try {
            data = data || {};
            data.action = action;

            let locationHref = window.location.href;
            let urlBase = locationHref.substring(0, locationHref.indexOf("?") > -1 ? locationHref.indexOf("?") : locationHref.length);
            const urlParams = new URLSearchParams(window.location.search);
            for (let [param, value] of Object.entries(data)) {
                urlParams.set(param, value);
            }

            const response = await fetch(`${urlBase}?${urlParams}`, {
                method: 'GET',
                cache: 'no-store',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'text/html'
                }
            });

            if (!response.ok) {
                throw new Error(response.statusText);
            }

            return response.text();
        } catch (error) {
            throw error;
        }
    },

    /**
     * GET de action global na raiz do host atual. Não usa a URL da página
     * (o catálogo /palestrantes devolve a listagem e pode vir de cache).
     * @param {string} action
     * @param {Record<string, string|number>} [data]
     * @returns {Promise<string>}
     */
    getGlobal: async (action, data) => {
        const url = new URL("/", window.location.origin);
        url.searchParams.set("action", action);
        Object.entries(data || {}).forEach(([param, value]) => {
            url.searchParams.set(param, String(value));
        });

        const response = await fetch(url.toString(), {
            method: "GET",
            cache: "no-store",
            credentials: "same-origin",
            headers: {
                Accept: "text/html",
                "X-Requested-With": "XMLHttpRequest",
            },
        });

        if (!response.ok) {
            throw new Error(response.statusText);
        }

        return response.text();
    },

    /**
     * Executa uma requisicao POST no servidor e retorna o HTML da resposta
     * @param {string} action    Nome da ação registrada no servidor a ser executada
     * @param {*} data           Dados a serem enviados para o servidor
     * @returns {Promise<string>}
     */
    post: async (action, data) => {
        try {
            data = data || {};
            data.action = action;

            let locationHref = window.location.href;
            let urlBase = locationHref.substring(0, locationHref.indexOf("?") > -1 ? locationHref.indexOf("?") : locationHref.length);

            const response = await fetch(`${urlBase}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'text/html'
                },
                body: new URLSearchParams(data || {})
            });

            if (!response.ok) {
                throw new Error(response.statusText);
            }

            return response.text();
        } catch (error) {
            throw error;
        }
    },

    /**
     * Executa uma requisicao GET no servidor e retorna um objeto JSON
     * @param {string} action   Nome da ação registrada no servidor a ser executada
     * @param {*} data          Dados a serem enviados para o servidor
     * @returns {Promise<ServerJsonResponse>}
     */
    jsonGet: async (action, data) => {
        try {
            data = data || {};
            data.action = action;

            const urlParams = new URLSearchParams(data);
            const response = await fetch(`${SITE_BASE_URL}/?${urlParams}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(response.statusText);
            }

            return response.json();
        } catch (error) {
            throw error;
        }
    },

    /**
     * Executa uma requisicao POST no servidor e retorna um objeto JSON
     * @param {string} action   Nome da ação registrada no servidor a ser executada
     * @param {*} data          Dados a serem enviados para o servidor
     * @returns {Promise<ServerJsonResponse>}
     */
    // Corpo enviado como application/x-www-form-urlencoded (não JSON): o backend só popula
    // $_POST a partir de form-urlencoded/multipart, um corpo application/json não seria lido.
    jsonPost: async (action, data) => {
        try {
            data = data || {};
            data.action = action;

            const response = await fetch(`${SITE_BASE_URL}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: new URLSearchParams(data || {})
            });

            if (!response.ok) {
                throw new Error(response.statusText);
            }

            return response.json();
        } catch (error) {
            throw error;
        }
    }
};
